import React from 'react';
import { type Task } from 'src/api/generated';
import { NewTaskForm } from 'src/modules/shared/components/molecules/tasks/new-task-form';
import { TaskItem } from 'src/modules/shared/components/molecules/tasks/task-item';
import { useTaskGrouping } from 'src/modules/shared/hooks/tasks/use-task-grouping';
import { PiCheck } from 'react-icons/pi';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';

interface TaskPanelProps {
  tasks: Task[];
  conversationId: number;
  isCreatingTask: boolean;
  onTaskCreated: () => void;
  onTaskCancel: () => void;
}

export const TaskPanel: React.FC<TaskPanelProps> = ({
  tasks,
  conversationId,
  isCreatingTask,
  onTaskCreated,
  onTaskCancel,
}) => {
  const { groupedTasks } = useTaskGrouping(tasks);

  const hasAnyTasks =
    groupedTasks.dateGroups.length > 0 || groupedTasks.statusGroups.length > 0;

  return (
    <div className="flex min-h-0 flex-col overflow-hidden bg-neutral-50">
      <div className="min-h-0 overflow-y-auto overflow-x-hidden bg-neutral-50 p-4">
        <div className="space-y-3">
          {isCreatingTask && (
            <NewTaskForm
              conversationId={conversationId}
              showConversationField={false}
              onTaskCreated={onTaskCreated}
              onTaskCancel={onTaskCancel}
            />
          )}

          {/* Date Groups (Active tasks) */}
          {groupedTasks.dateGroups.map((group) => (
            <div key={group.key} className="space-y-2">
              <div className="flex items-center gap-2">
                <Text
                  size="small"
                  weight="medium"
                  color={group.isOverdue ? 'danger' : 'primary'}
                >
                  {group.label}
                </Text>
                <Text size="small" className="text-gray-500">
                  ({group.tasks.length})
                </Text>
              </div>
              <div className="flex flex-col gap-2">
                {group.tasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    showConversationLink={false}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Status Groups (Completed/Cancelled tasks) */}
          {groupedTasks.statusGroups.map((group) => (
            <div key={group.status} className="space-y-2">
              <div className="flex items-center gap-2">
                <Text size="small" weight="medium" className="text-gray-700">
                  {group.label}
                </Text>
                <Text size="small" className="text-gray-500">
                  ({group.tasks.length})
                </Text>
              </div>
              <div className="flex flex-col gap-2">
                {group.tasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    showConversationLink={false}
                  />
                ))}
              </div>
            </div>
          ))}

          {!hasAnyTasks && !isCreatingTask && (
            <EmptyState
              // TODO: check when implementing i18n
              title="No hay tareas en esta conversación"
              icon={<PiCheck className="w-full h-full" />}
              placement="horizontal"
            />
          )}
        </div>
      </div>
    </div>
  );
};
